// client/src/utils/offlinePrime.js
// Warms the offline cache with the reference data a user needs to actually *do*
// something offline.
//
// Runtime caching alone is opportunistic: it only holds what the user happened to
// open while connected. A teacher who never opened Class 4B online has no student
// list for it, so marks and attendance can't be entered at all. Priming closes that
// gap by walking the data the user is entitled to and requesting it ahead of time.
//
// It deliberately issues ordinary GETs through fetchJson rather than writing to a
// store of its own: the service worker caches them exactly as it would a real page
// load, so each page's existing loaders then hit a warm cache with no page changes.

import { fetchJson } from './fetchJson';

// Don't re-walk the whole tree on every reconnect.
const MIN_INTERVAL_MS = 30 * 60 * 1000;
// School networks are often thin; keep the walk polite.
const CONCURRENCY = 3;

const LAST_RUN_KEY = 'offlinePrime:lastRun';

let running = false;

function lastRunAt() {
  try {
    return Number(localStorage.getItem(LAST_RUN_KEY)) || 0;
  } catch (_) {
    return 0;
  }
}

function markRun() {
  try {
    localStorage.setItem(LAST_RUN_KEY, String(Date.now()));
  } catch (_) { /* private mode */ }
}

// Every prime request is best-effort: a 403 for a role that lacks the page, or a
// 404 for a route that doesn't apply, must not abort the rest of the walk.
async function warm(path) {
  try {
    return await fetchJson(path);
  } catch (_) {
    return null;
  }
}

// Run tasks with a small concurrency cap rather than all at once.
async function pooled(items, worker) {
  const queue = [...items];
  const runners = Array.from({ length: Math.min(CONCURRENCY, queue.length) }, async () => {
    while (queue.length) {
      const item = queue.shift();
      await worker(item);
    }
  });
  await Promise.all(runners);
}

function idsFrom(payload, keys = ['id', 'classId', '_id']) {
  const rows = Array.isArray(payload) ? payload : (payload?.classes || payload?.data || payload?.rows || []);
  if (!Array.isArray(rows)) return [];
  return rows
    .map((r) => {
      if (r == null) return null;
      if (typeof r === 'number' || typeof r === 'string') return r;
      for (const k of keys) if (r[k] != null) return r[k];
      return null;
    })
    .filter((v) => v != null);
}

// Reference data every authenticated user benefits from having locally.
async function primeCommon() {
  await pooled(
    [
      '/api/terms',
      '/api/school-academic-years',
      '/api/global-settings',
    ],
    warm
  );
}

// A teacher's working set: their classes, and for each class the roster, subjects
// and evaluation windows that marks/attendance entry depend on.
async function primeTeacher() {
  await warm('/teacher/school-geolocation');
  await warm('/api/academics/evaluations/sequences/status');

  const classes = await warm('/teacher/classes');
  const classIds = idsFrom(classes);
  if (!classIds.length) return;

  await pooled(classIds, async (classId) => {
    const id = encodeURIComponent(classId);
    await warm(`/teacher/classes/${id}/students`);
    await warm(`/api/allocations/students/class/${id}`);
    await warm(`/api/academics/evaluations/class/${id}/evaluations`);

    // Marks entry loads the per-subject roster first and only falls back to the
    // whole-class list. For elective subjects those differ, so prime the rosters
    // too rather than letting offline silently show the wrong student set.
    const subjects = await warm(`/teacher/classes/${id}/subjects`);
    const subjectIds = idsFrom(subjects, ['id', 'subjectId', '_id']);
    for (const subjectId of subjectIds) {
      await warm(`/api/allocations/roster/${id}/${encodeURIComponent(subjectId)}`);
    }
  });
}

// THE OFFICE WORKING SET.
//
// Offline writing is useless without the reference data the write depends on:
// you cannot admit a pupil without the class list, generate a report without
// the marks and evaluations, print a receipt without the fee structure, or
// issue an ID card without the pupil record. Four endpoints covered none of
// that — an admin who went offline could see a class list and nothing else.
//
// Everything here is a plain GET, so it lands in the same read cache every page
// already reads from. Failures are individually swallowed by `warm`, so an
// endpoint this school does not use never blocks the rest.
async function primeSchoolAdmin() {
  await pooled(
    [
      // Who is in the school, and how it is organised.
      '/api/classes',
      '/api/subjects',
      '/api/students',
      '/api/users',
      // /api/staff is not a route. The staff list is served by
      // /api/staff-management/staff; only /api/staff/faculty, /leave and
      // /payroll exist under /api/staff, and none of them is a roll.
      '/api/staff-management/staff',
      '/api/guardians',

      // Academics — report generation and mark entry.
      '/api/academics/evaluations/sequences/status',
      '/api/report-cards/evaluations',
      '/api/grading-scales',
      '/api/terms',

      // Money — receipts, ledgers and what is owed.
      // Mounted under finance, not at the top level.
      '/api/finance/fee-structures',
      '/api/fee-types',
      '/api/finance/payments',
      '/api/invoices',
      '/api/expenses',
      '/api/budgets',

      // Enrolment and attendance summaries.
      // NO /api/enrollments ROUTE EXISTS, anywhere. Priming it spent a request
      // on a 404 and cached nothing, so every offline screen that needs
      // enrolment rows was empty with no sign of why. Removed rather than
      // guessed at: enrolments reach the client through /api/students today.
      '/api/attendance/summary',

      // Cards and documents.
      '/api/id-cards/templates',

      // Setup the pages read on load.
      '/api/school-setup-and-configuration/school-settings',
      '/api/school-setup-and-configuration/academic-sessions',
    ],
    warm
  );
}

const ROLE_PRIMERS = {
  Teacher: primeTeacher,
  ClassMaster: primeTeacher,
  SubjectHead: primeTeacher,
  HeadMaster: primeTeacher,
  Principal: primeTeacher,
  SchoolAdmin: primeSchoolAdmin,
  AdmissionsOfficer: primeSchoolAdmin,
};

/**
 * Warm the offline cache for the current user.
 *
 * @param {object}  user            current user (needs `role`)
 * @param {object}  [opts]
 * @param {boolean} [opts.force]    ignore the minimum interval
 * @returns {Promise<boolean>} whether a pass actually ran
 */
export async function primeOfflineData(user, { force = false } = {}) {
  if (!user || !user.role) return false;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return false;
  if (running) return false;
  if (!force && Date.now() - lastRunAt() < MIN_INTERVAL_MS) return false;

  running = true;
  try {
    try { window.dispatchEvent(new CustomEvent('app:prime:start')); } catch (_) {}

    await primeCommon();
    const primer = ROLE_PRIMERS[user.role];
    if (primer) await primer();

    markRun();
    try { window.dispatchEvent(new CustomEvent('app:prime:done')); } catch (_) {}
    return true;
  } catch (_) {
    // A failed pass is not an error the user needs to see; the next one retries.
    return false;
  } finally {
    running = false;
  }
}

// Clear the throttle so the next opportunity re-primes (e.g. after switching school
// or academic year, where the previously cached working set no longer applies).
export function resetPrimeThrottle() {
  try {
    localStorage.removeItem(LAST_RUN_KEY);
  } catch (_) { /* private mode */ }
}
